import React, { useState, useEffect } from 'react';
import tokenManager from '../utils/tokenManager';
import { X, Users, Activity, Plus, Trash2, RotateCcw, Power, Settings, Edit, Save, XCircle, Building, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import ICalSync from './ICalSync';

const SettingsModal = ({ onClose, currentUser, fullPage = false }) => {
  const [activeTab, setActiveTab] = useState(fullPage ? 'risk-templates' : 'users');
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoursFilter, setHoursFilter] = useState(24);
  const [limitFilter, setLimitFilter] = useState(100);
  const [cloudflareDevMode, setCloudflareDevMode] = useState(false);
  const [cloudflareTimeRemaining, setCloudflareTimeRemaining] = useState(0);
  const [prefillSettings, setPrefillSettings] = useState({
    defaultResponsibleTech: 'Sander Eriksen',
    defaultSupplier: 'Loveshack ANS',
    defaultSupplierId: 1
  });
  const [riskTemplates, setRiskTemplates] = useState([]);
  const [availableEffectTypes, setAvailableEffectTypes] = useState([]);
  const [newTemplate, setNewTemplate] = useState({
    activity: 'Opprigg',
    effect_type: '',
    hazard: '',
    cause: '',
    probability: 'Lav',
    consequence: 'Lav',
    measures: '',
    risk_level: 'Ok',
    is_outdoor_risk: false
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    logo_data: null
  });
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierFormData, setSupplierFormData] = useState({});

  useEffect(() => {
    if (activeTab === 'users') {
      loadAuthorizedUsers();
    } else if (activeTab === 'logs') {
      loadActivityLogs();
    } else if (activeTab === 'prefill') {
      loadPrefillSettings();
      loadSuppliers(); // Load suppliers for the dropdown
    } else if (activeTab === 'risk-templates') {
      loadRiskTemplates();
      loadAvailableEffectTypes();
    } else if (activeTab === 'suppliers') {
      loadSuppliers();
    }
  }, [activeTab]);

  // Sync supplier ID when suppliers are loaded and we have a defaultSupplier name
  useEffect(() => {
    if (suppliers.length > 0 && prefillSettings.defaultSupplier && !prefillSettings.defaultSupplierId) {
      const matchingSupplier = suppliers.find(s => s.name === prefillSettings.defaultSupplier);
      if (matchingSupplier) {
        updatePrefillSetting('defaultSupplierId', matchingSupplier.id);
      }
    }
  }, [suppliers, prefillSettings.defaultSupplier, prefillSettings.defaultSupplierId]);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadActivityLogs();
    }
  }, [hoursFilter, limitFilter]);

  const loadAuthorizedUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/authorized-emails', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Authorized users response:', data);
      
      // The API now returns the array directly
      setAuthorizedUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load authorized users');
      console.error('Error loading users:', error);
      setAuthorizedUsers([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        hours: hoursFilter.toString(),
        limit: limitFilter.toString()
      });
      
      const response = await fetch(`/api/activity-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Activity logs response:', data);
      
      // Make sure data is an array
      setActivityLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load activity logs');
      console.error('Error loading logs:', error);
      setActivityLogs([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };


  const addUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      const response = await fetch('/api/authorized-emails', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({ email: newUserEmail.trim() })
      });

      if (response.ok) {
        toast.success('User added successfully');
        setNewUserEmail('');
        loadAuthorizedUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add user');
      }
    } catch (error) {
      toast.error('Failed to add user');
      console.error('Error adding user:', error);
    }
  };

  const removeUser = async (email) => {
    if (!window.confirm(`Are you sure you want to remove ${email}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/authorized-emails/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (response.ok) {
        toast.success('User removed successfully');
        loadAuthorizedUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove user');
      }
    } catch (error) {
      toast.error('Failed to remove user');
      console.error('Error removing user:', error);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionColor = (action) => {
    if (action.includes('create') || action.includes('add')) return '#10b981';
    if (action.includes('delete') || action.includes('remove')) return '#ef4444';
    if (action.includes('update') || action.includes('edit')) return '#f59e0b';
    if (action.includes('undo')) return '#8b5cf6';
    return '#6b7280';
  };

  const isUndoable = (log) => {
    // Check if action is recent (within 24 hours)
    const logTime = new Date(log.timestamp);
    const now = new Date();
    const hoursDiff = (now - logTime) / (1000 * 60 * 60);
    
    if (hoursDiff > 24) return false;
    
    // Check if action is undoable
    const undoableActions = [
      'remove_authorized_user', 
      'add_authorized_user',
      'create_event',
      'delete_event',
      'create_effect', 
      'create_risk_entry',
      'delete_risk_entry',
      'add_effect_to_event',
      'upload_document',
      'create_setlist',
      'delete_setlist',
      'add_effect_to_setlist',
      'remove_effect_from_setlist',
      'delete_document',
      'create_inventory_sheet',
      'delete_inventory_sheet',
      'delete_inventory_item',
      'update_inventory_item'
    ];
    return undoableActions.includes(log.action);
  };

  const canUserUndo = (log) => {
    // Admin can undo any action, users can only undo their own
    return currentUser.email === 'san.eriksen@gmail.com' || currentUser.email === log.user_email;
  };

  const handleUndo = async (log) => {
    if (!canUserUndo(log)) {
      toast.error('You can only undo your own actions');
      return;
    }

    if (!isUndoable(log)) {
      toast.error('This action cannot be undone');
      return;
    }

    if (!window.confirm(`Are you sure you want to undo this action: ${log.action}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/undo-activity/${log.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Action undone successfully');
        
        // Refresh logs and users if needed
        if (activeTab === 'logs') {
          loadActivityLogs();
        }
        if (log.action === 'remove_authorized_user' || log.action === 'add_authorized_user') {
          loadAuthorizedUsers();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to undo action');
      }
    } catch (error) {
      toast.error('Failed to undo action');
      console.error('Error undoing action:', error);
    }
  };

  const loadCloudflareStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/cloudflare/development-mode', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          toast.error('Not authorized to access Cloudflare settings');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }
      
      const data = await response.json();
      setCloudflareDevMode(data.enabled);
      setCloudflareTimeRemaining(data.time_remaining);
    } catch (error) {
      toast.error('Failed to load Cloudflare status');
      console.error('Error loading Cloudflare status:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCloudflareDevMode = async () => {
    try {
      setLoading(true);
      const newState = !cloudflareDevMode;
      
      const response = await fetch('/api/cloudflare/development-mode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify({ enabled: newState })
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          toast.error('Not authorized to change Cloudflare settings');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }
      
      const data = await response.json();
      setCloudflareDevMode(data.enabled);
      setCloudflareTimeRemaining(data.time_remaining);
      
      toast.success(`Development mode ${data.enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to toggle Cloudflare development mode');
      console.error('Error toggling Cloudflare dev mode:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPrefillSettings = () => {
    try {
      const saved = localStorage.getItem('eventPrefillSettings');
      if (saved) {
        setPrefillSettings(prev => ({ ...prev, ...JSON.parse(saved), defaultSupplierId: JSON.parse(saved).defaultSupplierId || 1 }));
      }
    } catch (error) {
      console.error('Error loading prefill settings:', error);
    }
  };

  const savePrefillSettings = () => {
    try {
      localStorage.setItem('eventPrefillSettings', JSON.stringify(prefillSettings));
      toast.success('Prefill settings saved');
    } catch (error) {
      toast.error('Failed to save prefill settings');
      console.error('Error saving prefill settings:', error);
    }
  };

  const updatePrefillSetting = (field, value) => {
    const newSettings = {
      ...prefillSettings,
      [field]: value
    };
    setPrefillSettings(newSettings);
    // Auto-save to localStorage
    try {
      localStorage.setItem('eventPrefillSettings', JSON.stringify(newSettings));
    } catch (error) {
      console.error('Error saving prefill settings:', error);
    }
  };

  const loadRiskTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/risk-templates', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setRiskTemplates(data);
    } catch (error) {
      toast.error('Failed to load risk templates');
      console.error('Error loading risk templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableEffectTypes = async () => {
    // Use the same effect types as in the Effects Database
    const standardTypes = ["Waterfall", "Mine", "Gerb", "Spark", "Flame", "Comet", "Strobe", "Flash", "Confetti", "Other"];
    // Add special type for general/fallback risks
    const allTypes = [...standardTypes, 'default'];
    setAvailableEffectTypes(allTypes);
  };

  const createRiskTemplate = async (e) => {
    e.preventDefault();
    
    if (!newTemplate.effect_type || !newTemplate.hazard || !newTemplate.cause || !newTemplate.measures) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/risk-templates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(newTemplate)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const createdTemplate = await response.json();
      setRiskTemplates(prev => [...prev, createdTemplate]);
      
      // Reset form
      setNewTemplate({
        activity: 'Opprigg',
        effect_type: '',
        hazard: '',
        cause: '',
        probability: 'Lav',
        consequence: 'Lav',
        measures: '',
        risk_level: 'Ok',
        is_outdoor_risk: false
      });
      
      toast.success('Risk template created successfully');
    } catch (error) {
      toast.error('Failed to create risk template');
      console.error('Error creating risk template:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteRiskTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this risk template?')) {
      return;
    }

    try {
      const response = await fetch(`/api/risk-templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setRiskTemplates(prev => prev.filter(template => template.id !== templateId));
      toast.success('Risk template deleted successfully');
    } catch (error) {
      toast.error('Failed to delete risk template');
      console.error('Error deleting risk template:', error);
    }
  };

  const startEditTemplate = (template) => {
    setEditingTemplate(template.id);
    setEditFormData({
      activity: template.activity,
      effect_type: template.effect_type,
      hazard: template.hazard,
      cause: template.cause,
      probability: template.probability,
      consequence: template.consequence,
      measures: template.measures,
      risk_level: template.risk_level,
      is_outdoor_risk: template.is_outdoor_risk || false
    });
  };

  const cancelEditTemplate = () => {
    setEditingTemplate(null);
    setEditFormData({});
  };

  const updateRiskTemplate = async (templateId) => {
    if (!editFormData.effect_type || !editFormData.hazard || !editFormData.cause || !editFormData.measures) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/risk-templates/${templateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(editFormData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedTemplate = await response.json();
      setRiskTemplates(prev => prev.map(template => 
        template.id === templateId ? updatedTemplate : template
      ));
      
      setEditingTemplate(null);
      setEditFormData({});
      toast.success('Risk template updated successfully');
    } catch (error) {
      toast.error('Failed to update risk template');
      console.error('Error updating risk template:', error);
    } finally {
      setLoading(false);
    }
  };

  // Suppliers functions
  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/suppliers', {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setSuppliers(data);
    } catch (error) {
      toast.error('Failed to load suppliers');
      console.error('Error loading suppliers:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSupplier = async (e) => {
    e.preventDefault();
    if (!newSupplier.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/suppliers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(newSupplier)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const createdSupplier = await response.json();
      setSuppliers(prev => [...prev, createdSupplier]);
      
      // Reset form
      setNewSupplier({
        name: '',
        contact_email: '',
        contact_phone: '',
        address: '',
        logo_data: null
      });

      toast.success('Supplier created successfully');
    } catch (error) {
      toast.error('Failed to create supplier');
      console.error('Error creating supplier:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSupplier = async (supplierId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/suppliers/${supplierId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenManager.getToken()}`
        },
        body: JSON.stringify(supplierFormData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json(); // Just to consume the success response
      // Reload suppliers to get the updated data
      await loadSuppliers();
      
      setEditingSupplier(null);
      setSupplierFormData({});
      toast.success('Supplier updated successfully');
    } catch (error) {
      console.error('Error updating supplier:', error);
      console.error('Error details:', error.message);
      toast.error(`Failed to update supplier: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteSupplier = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSuppliers(prev => prev.filter(supplier => supplier.id !== supplierId));
      toast.success('Supplier deleted successfully');
    } catch (error) {
      toast.error('Failed to delete supplier');
      console.error('Error deleting supplier:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('Image must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target.result.split(',')[1]; // Remove data:image/png;base64, prefix
      
      if (isEdit) {
        setSupplierFormData(prev => ({
          ...prev,
          logo_data: base64String
        }));
      } else {
        setNewSupplier(prev => ({
          ...prev,
          logo_data: base64String
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const content = (
    <>
      {!fullPage && (
        <>
          <div className="modal-header">
            <h2>Settings</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
          </div>

          {/* Tab Navigation */}
          <div style={{
          display: 'flex', 
          borderBottom: '1px solid #e5e7eb', 
          marginBottom: '20px' 
        }}>
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'users' ? '#3b82f6' : 'transparent',
              color: activeTab === 'users' ? 'white' : '#6b7280',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Users size={16} />
            Users
          </button>
          <button
            className={`tab-button ${activeTab === 'logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('logs')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'logs' ? '#3b82f6' : 'transparent',
              color: activeTab === 'logs' ? 'white' : '#6b7280',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Activity size={16} />
            Activity Logs
          </button>
          <button
            className={`tab-button ${activeTab === 'prefill' ? 'active' : ''}`}
            onClick={() => setActiveTab('prefill')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'prefill' ? '#3b82f6' : 'transparent',
              color: activeTab === 'prefill' ? 'white' : '#6b7280',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Settings size={16} />
            Event Prefills
          </button>
          <button
            className={`tab-button ${activeTab === 'suppliers' ? 'active' : ''}`}
            onClick={() => setActiveTab('suppliers')}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: activeTab === 'suppliers' ? '#3b82f6' : 'transparent',
              color: activeTab === 'suppliers' ? 'white' : '#6b7280',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Building size={16} />
            Suppliers
          </button>
          
          </div>
        </>
        )}

        {fullPage && (
          <div className="risk-templates-page-header">
            <div>
              <h2>Risk Templates</h2>
              <p>Manage reusable hazards, controls, and risk ratings in a proper workspace.</p>
            </div>
            <button className="btn btn-secondary" onClick={onClose}>Back to Events</button>
          </div>
        )}

        {/* Tab Content */}
        <div style={{
          maxHeight: fullPage ? 'none' : '400px',
          overflowY: fullPage ? 'visible' : 'auto'
        }}>
          {activeTab === 'users' && (
            <div>
              <h3 style={{ marginBottom: '15px' }}>Authorized Users</h3>
              
              {/* Add User Form */}
              <form onSubmit={addUser} style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="form-control"
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="btn">
                    <Plus size={16} />
                    Add User
                  </button>
                </div>
              </form>

              {/* Users List */}
              {loading ? (
                <p>Loading users...</p>
              ) : authorizedUsers.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280' }}>
                  No authorized users found
                </p>
              ) : (
                <div>
                  {authorizedUsers.map((user) => (
                    <div
                      key={user.email}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        marginBottom: '8px'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600' }}>{user.email}</div>
                        {user.name && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            {user.name}
                          </div>
                        )}
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          Added: {formatTimestamp(user.created_at)}
                          {user.added_by && ` by ${user.added_by}`}
                        </div>
                      </div>
                      {user.email !== currentUser.email && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => removeUser(user.email)}
                          title="Remove User"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'logs' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0 }}>User Activity Logs</h3>
                
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500' }}>Time Range:</label>
                    <select
                      value={hoursFilter}
                      onChange={(e) => setHoursFilter(Number(e.target.value))}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value={1}>Last 1 hour</option>
                      <option value={6}>Last 6 hours</option>
                      <option value={24}>Last 24 hours</option>
                      <option value={72}>Last 3 days</option>
                      <option value={168}>Last week</option>
                      <option value={720}>Last month</option>
                      <option value={0}>All time</option>
                    </select>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontSize: '14px', fontWeight: '500' }}>Limit:</label>
                    <select
                      value={limitFilter}
                      onChange={(e) => setLimitFilter(Number(e.target.value))}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      <option value={50}>50 entries</option>
                      <option value={100}>100 entries</option>
                      <option value={200}>200 entries</option>
                      <option value={500}>500 entries</option>
                      <option value={0}>No limit</option>
                    </select>
                  </div>
                </div>
              </div>
              
              {loading ? (
                <p>Loading activity logs...</p>
              ) : (
                <div>
                  {activityLogs.map((log) => (
                    <div
                      key={log.id}
                      style={{
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        borderLeft: `4px solid ${getActionColor(log.action)}`,
                        backgroundColor: '#fafafa'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          {/* Header with user and action */}
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ fontWeight: '700', fontSize: '15px', color: '#1f2937' }}>
                              {log.user_name || log.user_email}
                            </div>
                            <span style={{ 
                              backgroundColor: getActionColor(log.action), 
                              color: 'white', 
                              padding: '4px 10px', 
                              borderRadius: '16px', 
                              fontSize: '12px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {log.action.replace(/_/g, ' ')}
                            </span>
                            {log.entity_type && (
                              <span style={{ 
                                color: '#6b7280', 
                                fontSize: '13px',
                                backgroundColor: '#f3f4f6',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontWeight: '500'
                              }}>
                                {log.entity_type.replace(/_/g, ' ')} {log.entity_id && `#${log.entity_id}`}
                              </span>
                            )}
                          </div>
                          
                          {/* Main details */}
                          {log.details && (
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#374151', 
                              marginBottom: '8px',
                              lineHeight: '1.5',
                              backgroundColor: '#ffffff',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid #e5e7eb'
                            }}>
                              <strong>Details:</strong> {log.details}
                            </div>
                          )}
                          
                          {/* Metadata row */}
                          <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: '12px', 
                            fontSize: '11px', 
                            color: '#9ca3af',
                            marginTop: '8px'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ fontWeight: '600' }}>Time:</span>
                              <span>{formatTimestamp(log.timestamp)}</span>
                            </div>
                            {log.ip_address && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ fontWeight: '600' }}>IP:</span>
                                <span>{log.ip_address}</span>
                              </div>
                            )}
                            {log.user_agent && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ fontWeight: '600' }}>Browser:</span>
                                <span>{log.user_agent.split(' ')[0] || 'Unknown'}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Undo button and status for undoable/undone actions */}
                          {(isUndoable(log) || log.action.startsWith('undo_')) && (
                            <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' }}>
                              {log.action.startsWith('undo_') ? (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 12px',
                                  backgroundColor: '#f3f4f6',
                                  color: '#6b7280',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  <RotateCcw size={14} />
                                  This action undid a previous action
                                </div>
                              ) : isUndoable(log) && canUserUndo(log) ? (
                                <button
                                  onClick={() => handleUndo(log)}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    backgroundColor: '#8b5cf6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                  }}
                                  onMouseOver={(e) => {
                                    e.target.style.backgroundColor = '#7c3aed';
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.target.style.backgroundColor = '#8b5cf6';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                                  }}
                                >
                                  <RotateCcw size={14} />
                                  Undo Action
                                </button>
                              ) : (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '6px 12px',
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  border: '1px solid #fcd34d',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600'
                                }}>
                                  <RotateCcw size={14} />
                                  Undoable (admin only)
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {activityLogs.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#6b7280' }}>
                      No activity logs found
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'cloudflare' && (
            <div>
              <h3 style={{ marginBottom: '20px' }}>Cloudflare Development Mode</h3>
              
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <p style={{ marginBottom: '15px', color: '#495057' }}>
                  Development mode temporarily bypasses Cloudflare's cache to show changes immediately. 
                  It automatically expires after 3 hours.
                </p>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '15px',
                  backgroundColor: 'white',
                  borderRadius: '6px',
                  border: '1px solid #dee2e6'
                }}>
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px',
                      marginBottom: '8px'
                    }}>
                      <h4 style={{ margin: 0 }}>Development Mode</h4>
                      {cloudflareDevMode && (
                        <span style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ACTIVE
                        </span>
                      )}
                    </div>
                    {cloudflareDevMode && cloudflareTimeRemaining > 0 && (
                      <p style={{ 
                        margin: 0, 
                        fontSize: '14px', 
                        color: '#6c757d' 
                      }}>
                        Time remaining: {Math.floor(cloudflareTimeRemaining / 60)} minutes
                      </p>
                    )}
                  </div>
                  
                  <button
                    onClick={toggleCloudflareDevMode}
                    disabled={loading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: cloudflareDevMode ? '#dc3545' : '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1,
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <Power size={16} />
                    {loading ? 'Loading...' : (cloudflareDevMode ? 'Disable' : 'Enable')}
                  </button>
                </div>
                
                {!currentUser || currentUser.email !== 'san.eriksen@gmail.com' ? (
                  <div style={{
                    marginTop: '15px',
                    padding: '10px',
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <strong>Note:</strong> Only administrators can toggle development mode.
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {activeTab === 'prefill' && (
            <div>
              <h3 style={{ marginBottom: '20px' }}>Event Default Values</h3>
              
              <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <p style={{ marginBottom: '20px', color: '#495057' }}>
                  Set default values that will automatically populate when creating new events.
                </p>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Default Responsible Technician
                    </label>
                    <input
                      type="text"
                      value={prefillSettings.defaultResponsibleTech}
                      onChange={(e) => updatePrefillSetting('defaultResponsibleTech', e.target.value)}
                      placeholder="Enter default technician name"
                      className="form-control"
                      style={{ 
                        padding: '12px',
                        fontSize: '14px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600',
                      color: '#374151'
                    }}>
                      Default Supplier
                    </label>
                    <select
                      value={prefillSettings.defaultSupplierId || ''}
                      onChange={(e) => {
                        const selectedSupplierId = e.target.value ? parseInt(e.target.value) : null;
                        const selectedSupplier = suppliers.find(s => s.id === selectedSupplierId);
                        updatePrefillSetting('defaultSupplierId', selectedSupplierId);
                        updatePrefillSetting('defaultSupplier', selectedSupplier ? selectedSupplier.name : '');
                      }}
                      className="form-control"
                      style={{ 
                        padding: '12px',
                        fontSize: '14px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db'
                      }}
                    >
                      <option value="">Select default supplier...</option>
                      {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '20px',
                  padding: '10px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>✓</span>
                    Settings are saved automatically
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'risk-templates' && (
            <div>
              <h3 style={{ marginBottom: '20px' }}>Risk Templates Management</h3>
              
              {/* Add New Template Form */}
              <form className="risk-template-form" onSubmit={createRiskTemplate} style={{
                marginBottom: '30px',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <h4 style={{ marginBottom: '15px' }}>Add New Risk Template</h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Activity</label>
                    <select
                      value={newTemplate.activity}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, activity: e.target.value }))}
                      className="form-control"
                      style={{ padding: '8px' }}
                    >
                      <option value="Opprigg">Opprigg</option>
                      <option value="Test">Test</option>
                      <option value="Avfyring">Avfyring</option>
                      <option value="Nedrigg">Nedrigg</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Effect Type</label>
                    <select
                      value={newTemplate.effect_type}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, effect_type: e.target.value }))}
                      className="form-control"
                      style={{ padding: '8px' }}
                      required
                    >
                      <option value="">Select Effect Type</option>
                      {availableEffectTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Hazard (Faremoment)</label>
                  <textarea
                    value={newTemplate.hazard}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, hazard: e.target.value }))}
                    placeholder="Describe the potential hazard"
                    className="form-control"
                    style={{ padding: '8px', minHeight: '60px' }}
                    required
                  />
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Cause (Årsak)</label>
                  <textarea
                    value={newTemplate.cause}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, cause: e.target.value }))}
                    placeholder="Describe what could cause this hazard"
                    className="form-control"
                    style={{ padding: '8px', minHeight: '60px' }}
                    required
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Probability</label>
                    <select
                      value={newTemplate.probability}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, probability: e.target.value }))}
                      className="form-control"
                      style={{ padding: '8px' }}
                    >
                      <option value="Lav">Lav</option>
                      <option value="Middels">Middels</option>
                      <option value="Høy">Høy</option>
                      <option value="Meget høy">Meget høy</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Consequence</label>
                    <select
                      value={newTemplate.consequence}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, consequence: e.target.value }))}
                      className="form-control"
                      style={{ padding: '8px' }}
                    >
                      <option value="Lav">Lav</option>
                      <option value="Middels">Middels</option>
                      <option value="Høy">Høy</option>
                      <option value="Meget høy">Meget høy</option>
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Risk Level</label>
                    <select
                      value={newTemplate.risk_level}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, risk_level: e.target.value }))}
                      className="form-control"
                      style={{ padding: '8px' }}
                    >
                      <option value="Ok">Ok</option>
                      <option value="Lav">Lav</option>
                      <option value="Middels">Middels</option>
                      <option value="Høy">Høy</option>
                      <option value="Meget høy">Meget høy</option>
                    </select>
                  </div>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newTemplate.is_outdoor_risk}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, is_outdoor_risk: e.target.checked }))}
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '600' }}>Outdoor Risk Template</span>
                  </label>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', marginLeft: '24px' }}>
                    Check this if this risk only applies to outdoor events
                  </div>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Measures (Tiltak)</label>
                  
                  {/* Template Variable Buttons */}
                  <div className="risk-template-variable-box" style={{
                    marginBottom: '10px', 
                    padding: '10px', 
                    backgroundColor: '#f0f9ff', 
                    borderRadius: '6px',
                    border: '1px solid #e0f2fe'
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: '#0369a1' }}>
                      Template Variables (click to insert):
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {[
                        { label: 'Event Name', var: '{event_name}' },
                        { label: 'Venue Height', var: '{venue_height}' },
                        { label: 'Venue Width', var: '{venue_width}' },
                        { label: 'Venue Depth', var: '{venue_depth}' },
                        { label: 'Event Date', var: '{event_date}' },
                        { label: 'Supplier', var: '{supplier}' },
                        { label: 'Responsible Tech', var: '{responsible_technician}' },
                        { label: 'Safety Distance', var: '{safety_distance}' },
                        { label: 'Effective Range', var: '{effective_range}' },
                        { label: 'Clearance Height', var: '{clearance_height}' }
                      ].map((item) => (
                        <button
                          key={item.var}
                          type="button"
                          onClick={() => {
                            const textarea = document.querySelector('textarea[placeholder*="safety measures"]');
                            if (textarea) {
                              const cursorPos = textarea.selectionStart;
                              const textBefore = newTemplate.measures.substring(0, cursorPos);
                              const textAfter = newTemplate.measures.substring(cursorPos);
                              setNewTemplate(prev => ({ 
                                ...prev, 
                                measures: textBefore + item.var + textAfter 
                              }));
                            } else {
                              setNewTemplate(prev => ({ 
                                ...prev, 
                                measures: prev.measures + item.var 
                              }));
                            }
                          }}
                          style={{
                            fontSize: '11px',
                            padding: '4px 8px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <textarea
                    value={newTemplate.measures}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, measures: e.target.value }))}
                    placeholder="Describe the safety measures and precautions (use template variables above for dynamic content)"
                    className="form-control"
                    style={{ padding: '8px', minHeight: '80px' }}
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="btn"
                  disabled={loading}
                  style={{
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '10px 20px'
                  }}
                >
                  <Plus size={16} style={{ marginRight: '8px' }} />
                  Add Template
                </button>
              </form>
              
              {/* Existing Templates */}
              <div>
                <h4 style={{ marginBottom: '15px' }}>Existing Templates ({riskTemplates.length})</h4>
                
                {loading ? (
                  <p>Loading templates...</p>
                ) : riskTemplates.length === 0 ? (
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                    No risk templates found. Create your first template above.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {riskTemplates.map((template) => (
                      <div
                        key={template.id}
                        className="risk-template-card"
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '15px',
                          backgroundColor: 'white'
                        }}
                      >
                        {editingTemplate === template.id ? (
                          // Edit Mode
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                              <h5 style={{ margin: 0, color: '#1f2937' }}>Editing Template</h5>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => updateRiskTemplate(template.id)}
                                  className="btn btn-sm"
                                  style={{ padding: '5px 8px', backgroundColor: '#10b981', color: 'white' }}
                                  title="Save changes"
                                >
                                  <Save size={14} />
                                </button>
                                <button
                                  onClick={cancelEditTemplate}
                                  className="btn btn-secondary btn-sm"
                                  style={{ padding: '5px 8px' }}
                                  title="Cancel editing"
                                >
                                  <XCircle size={14} />
                                </button>
                              </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Activity</label>
                                <select
                                  value={editFormData.activity}
                                  onChange={(e) => setEditFormData(prev => ({ ...prev, activity: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                >
                                  <option value="Opprigg">Opprigg</option>
                                  <option value="Test">Test</option>
                                  <option value="Avfyring">Avfyring</option>
                                  <option value="Nedrigg">Nedrigg</option>
                                </select>
                              </div>
                              
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Effect Type</label>
                                <select
                                  value={editFormData.effect_type}
                                  onChange={(e) => setEditFormData(prev => ({ ...prev, effect_type: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                  required
                                >
                                  <option value="">Select Effect Type</option>
                                  {availableEffectTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            
                            <div style={{ marginBottom: '15px' }}>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Hazard</label>
                              <textarea
                                value={editFormData.hazard}
                                onChange={(e) => setEditFormData(prev => ({ ...prev, hazard: e.target.value }))}
                                className="form-control"
                                style={{ padding: '6px', minHeight: '50px', fontSize: '14px' }}
                                required
                              />
                            </div>
                            
                            <div style={{ marginBottom: '15px' }}>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Cause</label>
                              <textarea
                                value={editFormData.cause}
                                onChange={(e) => setEditFormData(prev => ({ ...prev, cause: e.target.value }))}
                                className="form-control"
                                style={{ padding: '6px', minHeight: '50px', fontSize: '14px' }}
                                required
                              />
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Probability</label>
                                <select
                                  value={editFormData.probability}
                                  onChange={(e) => setEditFormData(prev => ({ ...prev, probability: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                >
                                  <option value="Lav">Lav</option>
                                  <option value="Middels">Middels</option>
                                  <option value="Høy">Høy</option>
                                  <option value="Meget høy">Meget høy</option>
                                </select>
                              </div>
                              
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Consequence</label>
                                <select
                                  value={editFormData.consequence}
                                  onChange={(e) => setEditFormData(prev => ({ ...prev, consequence: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                >
                                  <option value="Lav">Lav</option>
                                  <option value="Middels">Middels</option>
                                  <option value="Høy">Høy</option>
                                  <option value="Meget høy">Meget høy</option>
                                </select>
                              </div>
                              
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Risk Level</label>
                                <select
                                  value={editFormData.risk_level}
                                  onChange={(e) => setEditFormData(prev => ({ ...prev, risk_level: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                >
                                  <option value="Ok">Ok</option>
                                  <option value="Lav">Lav</option>
                                  <option value="Middels">Middels</option>
                                  <option value="Høy">Høy</option>
                                  <option value="Meget høy">Meget høy</option>
                                </select>
                              </div>
                            </div>
                            
                            <div style={{ marginBottom: '15px' }}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                                <input
                                  type="checkbox"
                                  checked={editFormData.is_outdoor_risk || false}
                                  onChange={(e) => setEditFormData(prev => ({ ...prev, is_outdoor_risk: e.target.checked }))}
                                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                                />
                                <span style={{ fontWeight: '600' }}>Outdoor Risk Template</span>
                              </label>
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px', marginLeft: '20px' }}>
                                Check this if this risk only applies to outdoor events
                              </div>
                            </div>
                            
                            <div>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Measures</label>
                              
                              {/* Template Variable Buttons for Edit Mode */}
                              <div className="risk-template-variable-box" style={{
                                marginBottom: '8px', 
                                padding: '8px', 
                                backgroundColor: '#f0f9ff', 
                                borderRadius: '4px',
                                border: '1px solid #e0f2fe'
                              }}>
                                <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '6px', color: '#0369a1' }}>
                                  Template Variables:
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                  {[
                                    { label: 'Event Name', var: '{event_name}' },
                                    { label: 'Venue Height', var: '{venue_height}' },
                                    { label: 'Venue Width', var: '{venue_width}' },
                                    { label: 'Venue Depth', var: '{venue_depth}' },
                                    { label: 'Event Date', var: '{event_date}' },
                                    { label: 'Supplier', var: '{supplier}' },
                                    { label: 'Responsible Tech', var: '{responsible_technician}' },
                                    { label: 'Safety Distance', var: '{safety_distance}' },
                                    { label: 'Effective Range', var: '{effective_range}' },
                                    { label: 'Clearance Height', var: '{clearance_height}' }
                                  ].map((item) => (
                                    <button
                                      key={item.var}
                                      type="button"
                                      onClick={() => {
                                        setEditFormData(prev => ({ 
                                          ...prev, 
                                          measures: prev.measures + item.var 
                                        }));
                                      }}
                                      style={{
                                        fontSize: '10px',
                                        padding: '3px 6px',
                                        backgroundColor: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {item.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              
                              <textarea
                                value={editFormData.measures}
                                onChange={(e) => setEditFormData(prev => ({ ...prev, measures: e.target.value }))}
                                className="form-control"
                                style={{ padding: '6px', minHeight: '70px', fontSize: '14px' }}
                                required
                              />
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                              <div>
                                <h5 style={{ margin: '0 0 5px 0', color: '#1f2937' }}>
                                  {template.activity} - {template.effect_type}
                                </h5>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                  Risk Level: {template.risk_level} | Created by: {template.created_by}
                                  {template.is_outdoor_risk && (
                                    <span style={{ marginLeft: '8px', color: '#059669', fontWeight: '600' }}>
                                      Outdoor only
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => startEditTemplate(template)}
                                  className="btn btn-sm"
                                  style={{ padding: '5px 8px', backgroundColor: '#3b82f6', color: 'white' }}
                                  title="Edit template"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => deleteRiskTemplate(template.id)}
                                  className="btn btn-danger btn-sm"
                                  style={{ padding: '5px 8px' }}
                                  title="Delete template"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                            
                            <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                              <div style={{ marginBottom: '8px' }}>
                                <strong>Hazard:</strong> {template.hazard}
                              </div>
                              <div style={{ marginBottom: '8px' }}>
                                <strong>Cause:</strong> {template.cause}
                              </div>
                              <div style={{ marginBottom: '8px' }}>
                                <strong>Probability:</strong> {template.probability} | <strong>Consequence:</strong> {template.consequence}
                              </div>
                              <div>
                                <strong>Measures:</strong> {template.measures}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'suppliers' && (
            <div>
              <h3 style={{ marginBottom: '20px' }}>Suppliers Management</h3>
              
              {/* Add New Supplier Form */}
              <form onSubmit={createSupplier} style={{ 
                marginBottom: '30px',
                backgroundColor: '#f8f9fa',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <h4 style={{ marginBottom: '15px' }}>Add New Supplier</h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Name *</label>
                    <input
                      type="text"
                      value={newSupplier.name}
                      onChange={(e) => setNewSupplier(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter supplier name"
                      className="form-control"
                      style={{ padding: '8px' }}
                      required
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Contact Email</label>
                    <input
                      type="email"
                      value={newSupplier.contact_email}
                      onChange={(e) => setNewSupplier(prev => ({ ...prev, contact_email: e.target.value }))}
                      placeholder="Enter contact email"
                      className="form-control"
                      style={{ padding: '8px' }}
                    />
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Contact Phone</label>
                    <input
                      type="text"
                      value={newSupplier.contact_phone}
                      onChange={(e) => setNewSupplier(prev => ({ ...prev, contact_phone: e.target.value }))}
                      placeholder="Enter contact phone"
                      className="form-control"
                      style={{ padding: '8px' }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLogoUpload(e, false)}
                      className="form-control"
                      style={{ padding: '8px' }}
                    />
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Upload company logo (max 5MB)
                    </div>
                  </div>
                </div>
                
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Address</label>
                  <textarea
                    value={newSupplier.address}
                    onChange={(e) => setNewSupplier(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Enter supplier address"
                    className="form-control"
                    style={{ padding: '8px', minHeight: '60px' }}
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="btn"
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Plus size={16} />
                  {loading ? 'Creating...' : 'Add Supplier'}
                </button>
              </form>

              {/* Suppliers List */}
              <div>
                <h4 style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Building size={18} />
                  Existing Suppliers ({suppliers.length})
                </h4>
                
                {loading && suppliers.length === 0 ? (
                  <p>Loading suppliers...</p>
                ) : (
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {suppliers.map(supplier => (
                      <div key={supplier.id} style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '20px',
                        backgroundColor: 'white'
                      }}>
                        {editingSupplier === supplier.id ? (
                          // Edit Mode
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Name</label>
                                <input
                                  type="text"
                                  value={supplierFormData.name}
                                  onChange={(e) => setSupplierFormData(prev => ({ ...prev, name: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                  required
                                />
                              </div>
                              
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Contact Email</label>
                                <input
                                  type="email"
                                  value={supplierFormData.contact_email}
                                  onChange={(e) => setSupplierFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                />
                              </div>
                            </div>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Contact Phone</label>
                                <input
                                  type="text"
                                  value={supplierFormData.contact_phone}
                                  onChange={(e) => setSupplierFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                />
                              </div>
                              
                              <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Logo</label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handleLogoUpload(e, true)}
                                  className="form-control"
                                  style={{ padding: '6px', fontSize: '14px' }}
                                />
                              </div>
                            </div>
                            
                            <div style={{ marginBottom: '15px' }}>
                              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Address</label>
                              <textarea
                                value={supplierFormData.address}
                                onChange={(e) => setSupplierFormData(prev => ({ ...prev, address: e.target.value }))}
                                className="form-control"
                                style={{ padding: '6px', minHeight: '50px', fontSize: '14px' }}
                              />
                            </div>
                            
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                              <button
                                onClick={() => updateSupplier(supplier.id)}
                                className="btn btn-sm"
                                style={{ padding: '5px 12px', backgroundColor: '#22c55e', color: 'white', display: 'flex', alignItems: 'center', gap: '4px' }}
                                disabled={loading}
                              >
                                <Save size={14} />
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingSupplier(null);
                                  setSupplierFormData({});
                                }}
                                className="btn btn-sm btn-secondary"
                                style={{ padding: '5px 12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                              >
                                <XCircle size={14} />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          // View Mode
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                  {supplier.logo_data && (
                                    <img 
                                      src={`data:image/png;base64,${supplier.logo_data}`}
                                      alt={`${supplier.name} logo`}
                                      style={{ 
                                        width: '40px', 
                                        height: '40px', 
                                        objectFit: 'contain',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '4px',
                                        padding: '4px'
                                      }}
                                    />
                                  )}
                                  <h5 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>{supplier.name}</h5>
                                </div>
                                
                                {supplier.contact_email && (
                                  <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                                    <strong>Email:</strong> {supplier.contact_email}
                                  </p>
                                )}
                                
                                {supplier.contact_phone && (
                                  <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                                    <strong>Phone:</strong> {supplier.contact_phone}
                                  </p>
                                )}
                                
                                {supplier.address && (
                                  <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                                    <strong>Address:</strong> {supplier.address}
                                  </p>
                                )}
                              </div>
                              
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => {
                                    setEditingSupplier(supplier.id);
                                    setSupplierFormData({
                                      name: supplier.name,
                                      contact_email: supplier.contact_email || '',
                                      contact_phone: supplier.contact_phone || '',
                                      address: supplier.address || '',
                                      logo_data: supplier.logo_data
                                    });
                                  }}
                                  className="btn btn-sm"
                                  style={{ padding: '5px 8px', backgroundColor: '#3b82f6', color: 'white' }}
                                  title="Edit supplier"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  onClick={() => deleteSupplier(supplier.id)}
                                  className="btn btn-sm btn-danger"
                                  style={{ padding: '5px 8px' }}
                                  title="Delete supplier"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {suppliers.length === 0 && !loading && (
                      <p style={{ textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>
                        No suppliers found. Add one above to get started.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'calendar' && (
            <ICalSync />
          )}
          
        </div>

        {!fullPage && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        )}
    </>
  );

  if (fullPage) {
    return <div className="risk-templates-page">{content}</div>;
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '800px', maxHeight: '90vh' }}>
        {content}
      </div>
    </div>
  );
};

export default SettingsModal;
